'use client';

import { Flex, Box } from '@chakra-ui/react';
import BlogPostCard from './BlogPostCard';
import LucideIcon from '@/components/ui/LucideIcon';
import { useState, useEffect } from 'react';
import { useBlogService } from '@/hooks/blog/useBlogPosts';
import { Carousel, type CarouselApi } from '@/components/ui/Carousel';

const BlogSwiper = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const { useGetBlogPostsSummary } = useBlogService();
  const { data: blogPosts } = useGetBlogPostsSummary({ limit: 6 });

  useEffect(() => {
    if (!api) {
      return;
    }

    const handleSelect = () => {
      setActiveIndex(api.selectedScrollSnap());
    };

    api.on('select', handleSelect);
    api.on('reInit', handleSelect);

    handleSelect();

    return () => {
      api.off('select', handleSelect);
    };
  }, [api]);

  return (
    <Box width="100%" display={{ base: 'none', tablet: 'flex', desktop: 'flex' }} flexDirection="column" gap={8}>
      <Box position="relative" width="100%" overflow="hidden">
        <Carousel
          setApi={setApi}
          opts={{
            loop: true,
            align: 'start',
            skipSnaps: false,
          }}
          w="100%"
        >
          <Carousel.Content>
            {blogPosts.map((post) => (
              <Carousel.Item
                key={post.id}
                flexBasis={{
                  desktop: '25%',
                  tablet: '50%',
                  base: '100%',
                }}
                px="12px"
              >
                <BlogPostCard
                  title={post.title}
                  image={post.thumbnailUrl1024x512}
                  date={post.publishedAt}
                  readTime={`${post.readingTimeMinutes} min read`}
                  link={post.url}
                />
              </Carousel.Item>
            ))}
          </Carousel.Content>
        </Carousel>
      </Box>
      <Flex alignItems="center" gap={6} justifyContent="center" width="100%">
        <Flex
          alignItems="center"
          cursor="pointer"
          justifyContent="center"
          onClick={() => api?.scrollPrev()}
          padding={2}
          transition="all 0.2s"
          _hover={{
            backgroundColor: 'neutral.5',
            borderColor: 'neutral.80',
            transform: 'translateX(-4px)',
          }}
        >
          <LucideIcon name="MoveLeft" width={54} height={24} color="neutral.100" strokeWidth={1} />
        </Flex>

        <Flex alignItems="center" gap={1}>
          {blogPosts.map((_, index) => (
            <Box
              backgroundColor={index === activeIndex ? 'neutral.100' : 'neutral.10'}
              borderRadius={index === activeIndex ? '4px' : 'full'}
              cursor="pointer"
              height={1}
              key={`indicator-${index}`}
              transition="all 0.2s"
              width={index === activeIndex ? 5 : 3}
              onClick={() => api?.scrollTo(index)}
            />
          ))}
        </Flex>

        <Flex
          alignItems="center"
          cursor="pointer"
          justifyContent="center"
          onClick={() => api?.scrollNext()}
          padding={2}
          transition="all 0.2s"
          _hover={{
            backgroundColor: 'neutral.5',
            borderColor: 'neutral.80',
            transform: 'translateX(4px)',
          }}
        >
          <LucideIcon name="MoveRight" width={54} height={24} color="neutral.100" strokeWidth={1} />
        </Flex>
      </Flex>
    </Box>
  );
};

export default BlogSwiper;
